import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { getErrorMessage } from '../utils/errorHandler';
import { 
  Loader2, Store, Building2, User, FileText, Upload, CheckCircle, 
  MapPin, Phone, Mail, ArrowLeft, ArrowRight, AlertCircle, Plus, Trash2, Clock
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const BecomeVendor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser, businesses } = useAuth();
  const { isLoaded: mapsLoaded } = useGoogleMaps();
  const [step, setStep] = useState(1); // 1 = confirmation, 2 = business details, 3 = verification docs
  const [loading, setLoading] = useState(false);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fullAddress, setFullAddress] = useState(''); // For Google Maps autocomplete
  
  // Draft businesses state
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(searchParams.get('resume') === 'true');
  const [selectedDraft, setSelectedDraft] = useState(null);
  
  // Check if this is adding another business (user already has businesses)
  const isAddingAnother = businesses && businesses.length > 0;

  // Form state - now includes email_choice for multi-business support
  const [formData, setFormData] = useState({
    business_name: '',
    description: '',
    contact_email: user?.email || '',
    email_choice: 'personal', // 'personal' or 'separate'
    contact_phone: '',
    street: '',
    city: '',
    province: '',
    postal_code: '',
    verification_type: 'individual',
    popi_consent: false
  });

  // File uploads
  const [files, setFiles] = useState({
    id_document: null,
    address_proof: null,
    bank_verification: null,
    registration_letter: null,
    business_address_proof: null,
    logo: null
  });

  useEffect(() => {
    // Pre-fill email from user account
    if (user?.email) {
      setFormData(prev => ({ ...prev, contact_email: user.email }));
    }
    
    // Fetch drafts if resuming
    if (showDrafts) {
      fetchDrafts();
    }
  }, [user, showDrafts]);

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const response = await axios.get(`${API}/user/businesses/drafts`, {
        withCredentials: true
      });
      setDrafts(response.data.drafts || []);
    } catch (err) {
      console.error('Failed to fetch drafts:', err);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleResumeDraft = (draft) => {
    setSelectedDraft(draft);
    // Pre-fill form with draft data
    setFormData({
      business_name: draft.business_name || '',
      description: draft.description || '',
      contact_email: draft.contact_email || user?.email || '',
      email_choice: draft.email_choice || 'personal',
      contact_phone: draft.contact_phone || '',
      street: draft.address?.street || '',
      city: draft.address?.city || '',
      province: draft.address?.province || '',
      postal_code: draft.address?.postal_code || '',
      verification_type: draft.verification_type || 'individual',
      popi_consent: draft.popi_consent || false
    });
    
    // Set full address for autocomplete
    if (draft.address) {
      setFullAddress(`${draft.address.street || ''}, ${draft.address.city || ''}, ${draft.address.province || ''}`);
    }
    
    setShowDrafts(false);
    setStep(2); // Go directly to business details
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;
    
    try {
      await axios.delete(`${API}/user/businesses/drafts/${draftId}`, {
        withCredentials: true
      });
      setDrafts(prev => prev.filter(d => d.business_id !== draftId));
    } catch (err) {
      alert(getErrorMessage(err));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({ ...prev, [fieldName]: file }));
    }
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add text fields
      formDataToSend.append('business_name', formData.business_name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('contact_email', formData.email_choice === 'personal' ? user?.email : formData.contact_email);
      formDataToSend.append('email_choice', formData.email_choice);
      formDataToSend.append('contact_phone', formData.contact_phone);
      formDataToSend.append('street', formData.street);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('province', formData.province);
      formDataToSend.append('postal_code', formData.postal_code);
      formDataToSend.append('verification_type', formData.verification_type);
      formDataToSend.append('popi_consent', formData.popi_consent);
      formDataToSend.append('upgrade_account', 'true'); // Signal to upgrade customer to vendor
      
      // Add files
      formDataToSend.append('id_document', files.id_document);
      formDataToSend.append('address_proof', files.address_proof);
      formDataToSend.append('bank_verification', files.bank_verification);
      
      if (formData.verification_type === 'business') {
        if (files.registration_letter) {
          formDataToSend.append('registration_letter', files.registration_letter);
        }
        if (files.business_address_proof) {
          formDataToSend.append('business_address_proof', files.business_address_proof);
        }
      }
      
      if (files.logo) {
        formDataToSend.append('logo', files.logo);
      }

      await axios.post(`${API}/business/register`, formDataToSend, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Delete the draft if we were resuming one
      if (selectedDraft?.business_id) {
        try {
          await axios.delete(`${API}/user/businesses/drafts/${selectedDraft.business_id}`, {
            withCredentials: true
          });
        } catch (e) {
          // Ignore draft deletion errors
          console.log('Draft cleanup failed:', e);
        }
      }

      setSuccess(true);
      
      // Refresh user data to get updated account type
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Registration failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = async () => {
    setError('');
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add text fields
      formDataToSend.append('business_name', formData.business_name || 'Untitled Business');
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('contact_email', formData.email_choice === 'personal' ? user?.email : formData.contact_email);
      formDataToSend.append('email_choice', formData.email_choice);
      formDataToSend.append('contact_phone', formData.contact_phone || '');
      formDataToSend.append('street', formData.street || '');
      formDataToSend.append('city', formData.city || '');
      formDataToSend.append('province', formData.province || '');
      formDataToSend.append('postal_code', formData.postal_code || '');
      formDataToSend.append('verification_type', formData.verification_type || 'individual');
      formDataToSend.append('popi_consent', 'false'); // Draft doesn't need consent
      formDataToSend.append('upgrade_account', 'true');
      formDataToSend.append('save_as_draft', 'true'); // Save as draft flag
      
      // Add files if available (for drafts, files are optional)
      if (files.id_document) formDataToSend.append('id_document', files.id_document);
      if (files.address_proof) formDataToSend.append('address_proof', files.address_proof);
      if (files.bank_verification) formDataToSend.append('bank_verification', files.bank_verification);
      if (files.registration_letter) formDataToSend.append('registration_letter', files.registration_letter);
      if (files.business_address_proof) formDataToSend.append('business_address_proof', files.business_address_proof);
      if (files.logo) formDataToSend.append('logo', files.logo);

      const response = await axios.post(`${API}/business/register`, formDataToSend, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // If we were resuming a draft, delete the old one
      if (selectedDraft?.business_id) {
        try {
          await axios.delete(`${API}/user/businesses/drafts/${selectedDraft.business_id}`, {
            withCredentials: true
          });
        } catch (e) {
          console.log('Old draft cleanup failed:', e);
        }
      }

      alert(`Draft saved successfully! You can continue later from the "Become a Vendor" page.`);
      navigate('/');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save draft. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const validateStep2 = () => {
    if (!formData.business_name.trim()) return 'Business name is required';
    if (!formData.description.trim()) return 'Description is required';
    if (!formData.contact_phone.trim()) return 'Contact phone is required';
    if (!formData.street.trim()) return 'Street address is required';
    if (!formData.city.trim()) return 'City is required';
    if (!formData.province.trim()) return 'Province is required';
    if (!formData.postal_code.trim()) return 'Postal code is required';
    return null;
  };

  const validateStep3 = () => {
    if (!files.id_document) return 'ID document is required';
    if (!files.address_proof) return 'Proof of address is required';
    if (!files.bank_verification) return 'Bank confirmation is required';
    if (formData.verification_type === 'business') {
      if (!files.registration_letter) return 'Business registration letter is required';
      if (!files.business_address_proof) return 'Business address proof is required';
    }
    if (!formData.popi_consent) return 'You must consent to the POPI Act terms';
    return null;
  };

  const handleNext = () => {
    setError('');
    
    if (step === 2) {
      const validationError = validateStep2();
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    
    if (step === 3) {
      const validationError = validateStep3();
      if (validationError) {
        setError(validationError);
        return;
      }
      handleSubmit();
      return;
    }
    
    setStep(step + 1);
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-white">Application Submitted!</CardTitle>
            <CardDescription className="text-gray-400">
              Your vendor application has been submitted for review
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-300">
                <strong className="text-yellow-500">What happens next?</strong>
              </p>
              <ul className="mt-2 space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                  Our team will review your documents within 24-48 hours
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                  You&apos;ll receive an email once your business is verified
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                  Your account has been upgraded to include vendor capabilities
                </li>
              </ul>
            </div>
            <Button 
              onClick={() => navigate('/business/dashboard')}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              Go to Business Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Draft Selection Screen */}
        {showDrafts && (
          <Card className="bg-gray-800 border-gray-700" data-testid="resume-registration-card">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
              <CardTitle className="text-2xl text-white" data-testid="resume-registration-title">Resume Registration</CardTitle>
              <CardDescription className="text-gray-400">
                Continue where you left off or start fresh
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingDrafts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
                </div>
              ) : drafts.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-400">Your incomplete registrations:</p>
                  {drafts.map((draft) => (
                    <div 
                      key={draft.business_id}
                      className="p-4 bg-gray-700/50 border border-gray-600 rounded-lg flex items-center justify-between"
                      data-testid={`draft-item-${draft.business_id}`}
                    >
                      <div>
                        <p className="text-white font-medium">
                          {draft.business_name || 'Unnamed Business'}
                        </p>
                        <p className="text-sm text-gray-400">
                          Started {new Date(draft.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleResumeDraft(draft)}
                          className="bg-yellow-500 hover:bg-yellow-600 text-black"
                          data-testid={`resume-draft-btn-${draft.business_id}`}
                        >
                          Resume
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteDraft(draft.business_id)}
                          className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                          data-testid={`delete-draft-btn-${draft.business_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <AlertCircle className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400" data-testid="no-drafts-message">No draft registrations found</p>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-700">
                <Button 
                  variant="outline" 
                  onClick={() => navigate(-1)}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go Back
                </Button>
                <Button 
                  onClick={() => setShowDrafts(false)}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                  data-testid="start-fresh-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Start Fresh
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Registration Flow */}
        {!showDrafts && (
          <>
            {/* Progress indicator */}
            <div className="flex items-center justify-center mb-8">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                      s === step 
                        ? 'bg-yellow-500 text-black' 
                        : s < step 
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {s < step ? <CheckCircle className="h-5 w-5" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-16 h-1 mx-2 ${s < step ? 'bg-green-500' : 'bg-gray-700'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

        {/* Step 1: Confirmation */}
        {step === 1 && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                {isAddingAnother ? <Plus className="h-8 w-8 text-yellow-500" /> : <Store className="h-8 w-8 text-yellow-500" />}
              </div>
              <CardTitle className="text-2xl text-white">
                {isAddingAnother ? 'Add Another Business' : 'Become a Vendor'}
              </CardTitle>
              <CardDescription className="text-gray-400">
                {isAddingAnother 
                  ? `You have ${businesses.length} business${businesses.length > 1 ? 'es' : ''}. Add another one to expand your portfolio.`
                  : 'Start earning by listing your PA systems and equipment on Gumba'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* User info */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-2">Logged in as:</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-black font-semibold">{user?.name?.charAt(0) || 'U'}</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{user?.name}</p>
                    <p className="text-sm text-gray-400">{user?.email}</p>
                    {isAddingAnother && (
                      <p className="text-xs text-yellow-500 mt-1">
                        {businesses.length} existing business{businesses.length > 1 ? 'es' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Existing businesses list (if adding another) */}
              {isAddingAnother && (
                <div className="bg-gray-700/30 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-2">Your current businesses:</p>
                  <div className="space-y-2">
                    {businesses.map((biz) => (
                      <div key={biz.business_id} className="flex items-center gap-2 text-gray-300 text-sm">
                        <Building2 className="h-4 w-4 text-yellow-500" />
                        <span>{biz.business_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          biz.verification_status === 'verified' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {biz.verification_status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Benefits */}
              <div className="space-y-3">
                <h3 className="text-white font-medium">
                  {isAddingAnother ? 'Why add another business?' : 'As a vendor, you can:'}
                </h3>
                <ul className="space-y-2">
                  {(isAddingAnother ? [
                    'Keep your businesses separate and organized',
                    'Each business has its own email and profile',
                    'Manage multiple equipment catalogs',
                    'Track bookings and earnings per business',
                    'Switch between businesses easily from the dashboard'
                  ] : [
                    'List your PA systems and equipment',
                    'Set your own prices and availability',
                    'Receive bookings directly from customers',
                    'Build your reputation with ratings & reviews',
                    'Access a dedicated business dashboard'
                  ]).map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {!isAddingAnother && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-300">
                      <p className="font-medium">Your account will be upgraded</p>
                      <p className="text-blue-400 mt-1">
                        You&apos;ll keep all your customer features and gain vendor capabilities. 
                        No need to create a new account!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => navigate(-1)}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go Back
                </Button>
                <Button 
                  onClick={() => setStep(2)}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                  data-testid="continue-to-details-btn"
                >
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Business Details */}
        {step === 2 && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Building2 className="h-5 w-5 text-yellow-500" />
                Business Information
              </CardTitle>
              <CardDescription className="text-gray-400">
                Tell us about your business
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label htmlFor="business_name" className="text-gray-300">Business Name *</Label>
                  <Input
                    id="business_name"
                    data-testid="business-name-input"
                    value={formData.business_name}
                    onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                    placeholder="e.g., Mofadi Sound Rentals"
                    className="bg-gray-700 border-gray-600 text-white mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-gray-300">Description *</Label>
                  <Textarea
                    id="description"
                    data-testid="business-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe your business and the services you offer..."
                    rows={3}
                    className="bg-gray-700 border-gray-600 text-white mt-1"
                  />
                </div>

                {/* Business Email Selection - Key multi-business feature */}
                <div className="pt-4 border-t border-gray-700">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Mail className="h-4 w-4 text-yellow-500" />
                    Business Contact Email
                  </h4>
                  <p className="text-sm text-gray-400 mb-3">
                    Choose which email address to use for this business. This email will receive customer inquiries and booking notifications.
                  </p>
                  
                  <RadioGroup 
                    value={formData.email_choice} 
                    onValueChange={(value) => setFormData({...formData, email_choice: value})}
                    className="space-y-3"
                  >
                    <div className={`flex items-start space-x-3 p-3 rounded-lg border transition-colors ${
                      formData.email_choice === 'personal' 
                        ? 'border-yellow-500 bg-yellow-500/10' 
                        : 'border-gray-700 hover:border-gray-600'
                    }`}>
                      <RadioGroupItem value="personal" id="email-personal" data-testid="email-choice-personal" className="mt-1" />
                      <label htmlFor="email-personal" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">Use my personal email</span>
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Recommended</span>
                        </div>
                        <p className="text-sm text-gray-400 mt-1">
                          Use <span className="text-yellow-500">{user?.email}</span> for this business.
                          No verification needed.
                        </p>
                      </label>
                    </div>
                    
                    <div className={`flex items-start space-x-3 p-3 rounded-lg border transition-colors ${
                      formData.email_choice === 'separate' 
                        ? 'border-yellow-500 bg-yellow-500/10' 
                        : 'border-gray-700 hover:border-gray-600'
                    }`}>
                      <RadioGroupItem value="separate" id="email-separate" data-testid="email-choice-separate" className="mt-1" />
                      <label htmlFor="email-separate" className="flex-1 cursor-pointer">
                        <span className="text-white font-medium">Use a separate business email</span>
                        <p className="text-sm text-gray-400 mt-1">
                          Enter a different email address for this business.
                          Verification will be required.
                        </p>
                      </label>
                    </div>
                  </RadioGroup>

                  {formData.email_choice === 'separate' && (
                    <div className="mt-4">
                      <Label htmlFor="contact_email" className="text-gray-300">Business Email *</Label>
                      <Input
                        id="contact_email"
                        data-testid="business-email-input"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                        placeholder="business@example.com"
                        className="bg-gray-700 border-gray-600 text-white mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        You&apos;ll need to verify this email before it becomes active.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="contact_phone" className="text-gray-300 flex items-center gap-1">
                    <Phone className="h-4 w-4" /> Contact Phone *
                  </Label>
                  <Input
                    id="contact_phone"
                    data-testid="business-phone-input"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="+27 12 345 6789"
                    className="bg-gray-700 border-gray-600 text-white mt-1"
                  />
                </div>

                {/* Address */}
                <div className="pt-4 border-t border-gray-700">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-yellow-500" />
                    Business Address
                  </h4>
                  <div className="space-y-4">
                    {/* Google Maps Address Autocomplete */}
                    {mapsLoaded && (
                      <AddressAutocomplete
                        label="Search Address"
                        value={fullAddress}
                        onChange={setFullAddress}
                        onPlaceSelect={(place) => {
                          if (place) {
                            setFormData(prev => ({
                              ...prev,
                              street: place.street || '',
                              city: place.city || '',
                              province: place.province || '',
                              postal_code: place.postal_code || ''
                            }));
                          }
                        }}
                        placeholder="Start typing to search for your business address..."
                      />
                    )}
                    <div>
                      <Label htmlFor="street" className="text-gray-300">Street Address *</Label>
                      <Input
                        id="street"
                        value={formData.street}
                        onChange={(e) => setFormData({...formData, street: e.target.value})}
                        placeholder="123 Main Street"
                        className="bg-gray-700 border-gray-600 text-white mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="text-gray-300">City *</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                          placeholder="Johannesburg"
                          className="bg-gray-700 border-gray-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="province" className="text-gray-300">Province *</Label>
                        <Select 
                          value={formData.province} 
                          onValueChange={(v) => setFormData({...formData, province: v})}
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-1">
                            <SelectValue placeholder="Select province" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            {['Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'].map(p => (
                              <SelectItem key={p} value={p} className="text-white hover:bg-gray-700">{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="w-1/2">
                      <Label htmlFor="postal_code" className="text-gray-300">Postal Code *</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                        placeholder="2000"
                        className="bg-gray-700 border-gray-600 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleSaveAsDraft}
                  disabled={loading}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  data-testid="save-draft-step2-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Clock className="h-4 w-4 mr-2" />
                  )}
                  Save Draft
                </Button>
                <Button 
                  onClick={handleNext}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Verification Documents */}
        {step === 3 && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-yellow-500" />
                Verification Documents
              </CardTitle>
              <CardDescription className="text-gray-400">
                Upload documents to verify your business
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Verification Type */}
              <div>
                <Label className="text-gray-300 mb-2 block">Verification Type *</Label>
                <Select 
                  value={formData.verification_type} 
                  onValueChange={(v) => setFormData({...formData, verification_type: v})}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="individual" className="text-white hover:bg-gray-700">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Individual / Sole Proprietor
                      </div>
                    </SelectItem>
                    <SelectItem value="business" className="text-white hover:bg-gray-700">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Registered Business / Company
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Document Uploads */}
              <div className="space-y-4">
                <h4 className="text-white font-medium">Required Documents</h4>
                
                {/* ID Document */}
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <Label className="text-gray-300 flex items-center gap-2 mb-2">
                    <Upload className="h-4 w-4" />
                    ID Document * (SA ID or Passport)
                  </Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'id_document')}
                    className="bg-gray-600 border-gray-500 text-white file:bg-yellow-500 file:text-black file:border-0 file:mr-3"
                  />
                  {files.id_document && (
                    <p className="text-sm text-green-400 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> {files.id_document.name}
                    </p>
                  )}
                </div>

                {/* Proof of Address */}
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <Label className="text-gray-300 flex items-center gap-2 mb-2">
                    <Upload className="h-4 w-4" />
                    Proof of Address * (Utility bill, bank statement)
                  </Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'address_proof')}
                    className="bg-gray-600 border-gray-500 text-white file:bg-yellow-500 file:text-black file:border-0 file:mr-3"
                  />
                  {files.address_proof && (
                    <p className="text-sm text-green-400 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> {files.address_proof.name}
                    </p>
                  )}
                </div>

                {/* Bank Verification */}
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <Label className="text-gray-300 flex items-center gap-2 mb-2">
                    <Upload className="h-4 w-4" />
                    Bank Confirmation Letter *
                  </Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'bank_verification')}
                    className="bg-gray-600 border-gray-500 text-white file:bg-yellow-500 file:text-black file:border-0 file:mr-3"
                  />
                  {files.bank_verification && (
                    <p className="text-sm text-green-400 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> {files.bank_verification.name}
                    </p>
                  )}
                </div>

                {/* Business-specific documents */}
                {formData.verification_type === 'business' && (
                  <>
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <Label className="text-gray-300 flex items-center gap-2 mb-2">
                        <Upload className="h-4 w-4" />
                        Company Registration Letter / CIPC Document *
                      </Label>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'registration_letter')}
                        className="bg-gray-600 border-gray-500 text-white file:bg-yellow-500 file:text-black file:border-0 file:mr-3"
                      />
                      {files.registration_letter && (
                        <p className="text-sm text-green-400 mt-1 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> {files.registration_letter.name}
                        </p>
                      )}
                    </div>
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <Label className="text-gray-300 flex items-center gap-2 mb-2">
                        <Upload className="h-4 w-4" />
                        Business Address Proof *
                      </Label>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'business_address_proof')}
                        className="bg-gray-600 border-gray-500 text-white file:bg-yellow-500 file:text-black file:border-0 file:mr-3"
                      />
                      {files.business_address_proof && (
                        <p className="text-sm text-green-400 mt-1 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> {files.business_address_proof.name}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Optional Logo */}
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <Label className="text-gray-300 flex items-center gap-2 mb-2">
                    <Upload className="h-4 w-4" />
                    Business Logo (Optional)
                  </Label>
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'logo')}
                    className="bg-gray-600 border-gray-500 text-white file:bg-yellow-500 file:text-black file:border-0 file:mr-3"
                  />
                  {files.logo && (
                    <p className="text-sm text-green-400 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> {files.logo.name}
                    </p>
                  )}
                </div>
              </div>

              {/* POPI Consent */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="popi_consent"
                    checked={formData.popi_consent}
                    onCheckedChange={(checked) => setFormData({...formData, popi_consent: checked})}
                    className="mt-1 border-blue-400 data-[state=checked]:bg-blue-500"
                  />
                  <label htmlFor="popi_consent" className="text-sm text-blue-300 cursor-pointer">
                    I consent to the collection, storage, and processing of my personal information in accordance 
                    with the Protection of Personal Information Act (POPIA). I understand that my documents will 
                    be used solely for verification purposes.
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(2)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  disabled={loading}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleSaveAsDraft}
                  disabled={loading}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  data-testid="save-draft-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Clock className="h-4 w-4 mr-2" />
                  )}
                  Save as Draft
                </Button>
                <Button 
                  onClick={handleNext}
                  disabled={loading}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                  data-testid="submit-application-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <CheckCircle className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default BecomeVendor;
